import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";
import type {
  ChatHistoryItem,
  ChatHistoryResponse,
  WorkflowDetailsResponse,
} from "../types/chatHistory";

// ---- Async Thunk to fetch chat history ----
export const fetchChatHistory = createAsyncThunk(
  "chatHistory/fetchChatHistory",
  async (
    {
      parentAgent,
      limit = 20,
      offset = 0,
    }: { parentAgent: string; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.post<ChatHistoryResponse>(
        `/user-chat-history-summary?limit=${limit}&offset=${offset}`,
        { parent_agent: parentAgent }
      );
      return { data: response.data, offset };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch chat history"
      );
    }
  }
);

// ---- Async Thunk to fetch workflow details by workflow ID ----
export const fetchWorkflowDetails = createAsyncThunk(
  "chatHistory/fetchWorkflowDetails",
  async (workflowId: string, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<WorkflowDetailsResponse>(
        `/user-chat-history-workflow-id?workflow_id=${workflowId}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch workflow details"
      );
    }
  }
);

interface ChatHistoryState {
  data: ChatHistoryItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  currentOffset: number;
  currentAgent: string | null;
}

const initialState: ChatHistoryState = {
  data: [],
  loading: false,
  loadingMore: false,
  error: null,
  hasMore: false,
  currentOffset: 0,
  currentAgent: null,
};

const chatHistorySlice = createSlice({
  name: "chatHistory",
  initialState,
  reducers: {
    resetChatHistory(state) {
      state.data = [];
      state.loading = false;
      state.loadingMore = false;
      state.error = null;
      state.hasMore = false;
      state.currentOffset = 0;
      state.currentAgent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatHistory.pending, (state, action) => {
        const { offset } = action.meta.arg;
        if (offset === 0) {
          state.loading = true;
          state.error = null;
        } else {
          state.loadingMore = true;
        }
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        const { data, offset } = action.payload;

        if (offset === 0) {
          // Initial load
          state.data = data.data;
          state.loading = false;
        } else {
          // Load more
          state.data = [...state.data, ...data.data];
          state.loadingMore = false;
        }

        state.hasMore = data.pagination.hasMore;
        state.currentOffset = offset;
        state.currentAgent = action.meta.arg.parentAgent;
        state.error = null;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error =
          (action.payload as string) || "Failed to fetch chat history";
      });
  },
});

export const { resetChatHistory } = chatHistorySlice.actions;
export default chatHistorySlice.reducer;
